// @ai-generated 2026-08-21T00:00:00Z by niklas.eicker@tngtech.com using Claude Opus 5 (1M context)
/**
 * Reads the payload out of a tool result.
 *
 * A command writes the `Result<T>` envelope — `{ data, messages }` — into the text of an MCP
 * `CallToolResult`, but that wrapper does not survive the agent loop intact: a single-block result
 * reaches the chat already unwrapped to one `TextContent`, as `{ text, annotations }`. Hence the
 * carriers below rather than one fixed path. Everything here is defensive: a card that cannot read
 * its payload falls back to the generic rendering rather than breaking the transcript.
 */
export function readCommandEnvelope(result: unknown): Record<string, unknown> | undefined {
    const payload = parseDeep(result);

    for (const text of textCarriers(payload)) {
        const envelope = parseDeep(text);
        if (isEnvelope(envelope)) {
            return envelope;
        }
    }

    return isEnvelope(payload) ? payload : undefined;
}

export function readCommandData(result: unknown): unknown {
    const envelope = readCommandEnvelope(result);
    if (envelope !== undefined && 'data' in envelope) {
        return envelope.data;
    }

    // A server that fills structuredContent hands over the payload without the text detour.
    const payload = parseDeep(result);
    if (isRecord(payload) && isRecord(payload.structuredContent)) {
        return payload.structuredContent.data ?? payload.structuredContent;
    }

    return undefined;
}

/**
 * The value a tool result encodes, seen through however many times it was JSON-encoded on its way
 * through the agent loop. A result that was not encoded at all is returned unchanged.
 */
export function readToolResultPayload(result: unknown): unknown {
    return parseDeep(result);
}

/** Mirrors the server's HasUnsuccessfulMessage: anything above Information made the call fail. */
const UNSUCCESSFUL_LEVELS = new Set(['Warning', 'Danger']);

/** One reason a command failed, as the `Result<T>` envelope reports it. */
export interface CommandMessage {
    level: string;
    /** What the message is about — a ruleset title for a validation failure. May be empty. */
    title: string;
    /** The reason itself, naming the entity it concerns. */
    message: string;
}

/**
 * The messages that made a command fail, in envelope order.
 *
 * These are what a batch command reports per failing entity, so they are the only place the chat can
 * read *which* records were rejected and why — the payload itself says only that the batch failed.
 */
export function readUnsuccessfulMessages(result: unknown): CommandMessage[] {
    const messages = readCommandEnvelope(result)?.messages;
    if (!Array.isArray(messages)) {
        return [];
    }

    return messages.filter(isRecord).flatMap((message) => {
        const level = String(message.level);
        if (!UNSUCCESSFUL_LEVELS.has(level)) {
            return [];
        }
        return [
            {
                level,
                title: typeof message.title === 'string' ? message.title : '',
                message: typeof message.message === 'string' ? message.message : '',
            },
        ];
    });
}

/**
 * Whether the envelope carries a message that made the command fail.
 *
 * Needed alongside the `isError` flag because that flag lives on the `CallToolResult` wrapper, which
 * a single-block result loses on its way to the chat — leaving the envelope's own messages as the
 * only surviving evidence that the call did not succeed.
 */
export function hasUnsuccessfulMessage(result: unknown): boolean {
    return readUnsuccessfulMessages(result).length > 0;
}

/** One entity a ruleset rejected, as `RulesetValidationOutcome` reports it. */
export interface ValidationFailure {
    entityId?: string;
    /** Human-readable identifier, absent when the entity's shape exposes none. */
    entityLabel?: string;
    rulesetTitle: string;
    message: string;
}

/** What a validation call decided: who passed, and who was rejected for what. */
export interface ValidationOutcome {
    passedCount: number;
    softFailed: ValidationFailure[];
    hardFailed: ValidationFailure[];
}

/**
 * Reads a validation command's verdict.
 *
 * The payload survives a rejected call on purpose — the reasons are the point of the call — so this
 * works on both the failing and the soft-failing result. Returns undefined for anything that is not
 * a validation outcome, so a caller can fall back rather than render an empty table.
 */
export function readValidationOutcome(result: unknown): ValidationOutcome | undefined {
    const data = readCommandData(result);
    if (!isRecord(data) || !('status' in data)) {
        return undefined;
    }

    return {
        passedCount: Array.isArray(data.passedIds) ? data.passedIds.length : 0,
        softFailed: readFailures(data.softFailed),
        hardFailed: readFailures(data.hardFailed),
    };
}

function readFailures(value: unknown): ValidationFailure[] {
    if (!Array.isArray(value)) {
        return [];
    }

    return value.filter(isRecord).map((failure) => ({
        ...(typeof failure.entityId === 'string' ? { entityId: failure.entityId } : {}),
        ...(typeof failure.entityLabel === 'string' && failure.entityLabel !== ''
            ? { entityLabel: failure.entityLabel }
            : {}),
        rulesetTitle: typeof failure.rulesetTitle === 'string' ? failure.rulesetTitle : '',
        message: typeof failure.message === 'string' ? failure.message : '',
    }));
}

function isEnvelope(value: unknown): value is Record<string, unknown> {
    return isRecord(value) && ('data' in value || 'messages' in value);
}

/** The text of every content block, whether wrapped, listed, or handed over on its own. */
function textCarriers(payload: unknown): string[] {
    const blocks = Array.isArray(payload)
        ? payload
        : isRecord(payload) && Array.isArray(payload.content)
          ? payload.content
          : [payload];

    return blocks.filter(isRecord).flatMap((block) => (typeof block.text === 'string' ? [block.text] : []));
}

/**
 * The records a command returned, plus how many exist ignoring paging. A search reports a total that
 * can exceed the page it returned; a command that creates records reports only what it created.
 */
export interface EntityPage {
    totalItemCount: number;
    items: Record<string, unknown>[];
}

/**
 * Reads the records out of a command result, whichever of the three shapes a command returns: a page
 * (`{ totalItemCount, items }`, as the searches return), a bare list (as invoices.generate
 * and the batch writes return), or a single record (as the singular writes like order:approve do).
 */
export function readEntityPage(result: unknown): EntityPage | undefined {
    const data = readCommandData(result);

    if (Array.isArray(data)) {
        const items = data.filter(isRecord);
        return { totalItemCount: items.length, items };
    }

    if (!isRecord(data)) {
        return undefined;
    }

    if (!Array.isArray(data.items)) {
        // One record is a page of one. Guarded on `id` so an arbitrary object — a status, a verdict —
        // is not mistaken for a record and rendered as a one-row table of empty cells.
        return typeof data.id === 'string' ? { totalItemCount: 1, items: [data] } : undefined;
    }

    const items = data.items.filter(isRecord);
    return {
        totalItemCount: typeof data.totalItemCount === 'number' ? data.totalItemCount : items.length,
        items,
    };
}

/**
 * Parses through repeated JSON-string encoding. A result that was serialized once more on its way
 * through the agent loop arrives as a string holding a string, and one `JSON.parse` leaves a string
 * behind rather than the object the caller needs. Stops at the first value that is not JSON.
 */
function parseDeep(value: unknown, depth = 3): unknown {
    let current = value;
    for (let step = 0; step < depth && typeof current === 'string'; step++) {
        const parsed = tryParseJson(current);
        if (parsed === undefined) {
            return current;
        }
        current = parsed;
    }
    return current;
}

function tryParseJson(value: string): unknown {
    try {
        return JSON.parse(value);
    } catch {
        return undefined;
    }
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null;
}
